import { useCallback, useContext, useEffect, useState } from "react";
import { Form as FormAntd, message } from "antd";
import { UploadOutlined, PaperClipOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ActionButton } from "@hdi-seguros/web-components.action-button";
import {
  ColSize,
  ContentSection,
  DatePickerForm,
  Form,
  InputCurrencyForm,
  InputForm,
  InputNumberForm,
  RowSize,
  SelectForm,
} from "../../../../../../../shared/components";
import { Modal } from "@hdi-seguros/web-components.modal";
import { UploadFileModal } from "../PaymentMethodSection/components/UploadFileModal";
import { StepThreeFormContext } from "../../contexts/StepThreeForm";
import { GenericDataFormContext } from "../../../../shared/contexts/GenericDataForm";
import { displayErrorMessage, ValidateForm } from "../../../../../../../shared/utils";
import { PAYMENT_METHODS } from "../../../../shared/utils/methods-payment.enum";
import { PaymentMethodsAdded } from "../../interfaces";
import { DocumentClass } from "../../../../../../../shared/interfaces/enums/document-class-enum";
import { DocumentUploadServiceRequestParams } from "../../../../../../../shared/interfaces/services/post-send-claim-documents-service";
import { AuthContext } from "../../../../../../../shared/contexts";
import { useFileHandler } from "../../hooks/useFileHandler";
import { useFileNameHandler } from "../../hooks/useFileNameHandler";

interface EditPaymentMethodModalProps {
  open: boolean;
  onCancel: () => void;
  paymentMethod: PaymentMethodsAdded | null;
  onSuccess: () => void;
}

export const EditPaymentMethodModal = ({
  open,
  onCancel,
  paymentMethod,
  onSuccess,
}: EditPaymentMethodModalProps) => {
  const validateForm = new ValidateForm();
  const [form] = FormAntd.useForm();
  const [canShowUploadModal, setCanShowUploadModal] = useState<boolean>(false);
  const [fileSelected, setFileSelected] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPaymentMethodCode, setCurrentPaymentMethodCode] = useState<string | undefined>(undefined);

  const {
    getPaymentMethodsOptions,
    getBanksOptions,
    banksOptions,
    paymentMethods,
    getAllBanksOptions,
    allBanksOptions,
    paymentMethodsDataTable,
    setPaymentMethodsDataTable,
    paymentsToLoad,
    setPaymentsToLoad,
    filesToSend,
    setFilesToSend,
    setHasChanges,
    getBankLabel,
    paymentsList,
  } = useContext(StepThreeFormContext);

  const { cutText } = useFileHandler(undefined);

  const { preliquidationInfo } = useContext(GenericDataFormContext);
  const authContext = useContext(AuthContext);
  const { openFile } = useFileHandler(form);
  const { setFileNameByPaymentMethod: setFileNameHandler } = useFileNameHandler();

  const paymentSelected = FormAntd.useWatch("paymentMethod", form);
  const currentPaymentMethod = paymentSelected || currentPaymentMethodCode;
  
  const resolveBankCodeAndLabel = useCallback(
    (bankValue: any, paymentMethodCode: number) => {
      const options =
        paymentMethodCode === PAYMENT_METHODS.ELECTRONIC_CHECK
          ? allBanksOptions || []
          : banksOptions || [];

      const rawValue = bankValue?.toString?.() ?? "";
      if (!rawValue) return { code: "", label: "" };

      const byValue = options.find((option: any) => String(option.value) === rawValue);
      if (byValue) return { code: String(byValue.value), label: byValue.label || "" };

      const byLabel = options.find((option: any) => option.label === rawValue);
      if (byLabel) return { code: String(byLabel.value), label: byLabel.label || "" };

      return { code: rawValue, label: rawValue };
    },
    [allBanksOptions, banksOptions]
  );

  useEffect(() => {
    if (open && paymentMethod) {
      const codePaymentMethod =
        paymentMethod.codePaymentMethod ||
        (typeof paymentMethod.paymentMethod === "number"
          ? paymentMethod.paymentMethod
          : typeof paymentMethod.paymentMethod === "string"
          ? Number(paymentMethod.paymentMethod.split("-")[0])
          : undefined);

      const formValues: any = {
        paymentMethod: codePaymentMethod?.toString() || "",
        exactAmount: paymentMethod.exactAmount,
      };

      if (paymentMethod.bank) {
        const resolvedBank = resolveBankCodeAndLabel(
          paymentMethod.bank,
          Number(codePaymentMethod)
        );
        formValues.bank = resolvedBank.code || paymentMethod.bank?.toString();
      }

      if (paymentMethod.transferDate) {
        formValues.transferDate = dayjs(paymentMethod.transferDate);
      }

      if (paymentMethod.checkDate) {
        formValues.checkDate = dayjs(paymentMethod.checkDate);
      }

      if (paymentMethod.checkNumber) {
        formValues.checkNumber = paymentMethod.checkNumber;
      }

      if (paymentMethod.observations && paymentMethod.observations !== "-") {
        formValues.observations = paymentMethod.observations;
      }

      form.setFieldsValue(formValues);
      setCurrentPaymentMethodCode(codePaymentMethod?.toString());
      setFileSelected([]);
    } else if (!open) {
      setCurrentPaymentMethodCode(undefined);
    }
  }, [open, paymentMethod, form, resolveBankCodeAndLabel]);

  useEffect(() => {
    if (open) {
      getPaymentMethodsOptions();
      getBanksOptions();
      getAllBanksOptions();
    }
  }, [open, getPaymentMethodsOptions, getBanksOptions, getAllBanksOptions]);

  const generateFileNameForEdit = useCallback(
    (fileSelected: any[]) => {
      if (!fileSelected?.[0]?.name) return "Ningún archivo seleccionado...";

      const preliquidationNumber = preliquidationInfo?.numeroPreliquidacion!;
      const formValues = form.getFieldsValue();

      const codePaymentMethodValue =
        Number(formValues.paymentMethod) ||
        Number(currentPaymentMethodCode) ||
        paymentMethod?.codePaymentMethod;
      const codePaymentMethod =
        codePaymentMethodValue && !isNaN(codePaymentMethodValue)
          ? codePaymentMethodValue
          : 0;

      const resolvedBank = resolveBankCodeAndLabel(formValues.bank, codePaymentMethod);
      const bankSelectedName: string = (
        resolvedBank.label ||
        (typeof paymentMethod?.bank === "string" ? paymentMethod.bank : "")
      ).replace(/\s+/g, "");

      const transferDate = formValues.transferDate
        ? dayjs(formValues.transferDate).format("YYYY-MM-DD")
        : paymentMethod?.transferDate
        ? dayjs(paymentMethod.transferDate).format("YYYY-MM-DD")
        : "";
      const checkDate = formValues.checkDate
        ? dayjs(formValues.checkDate).format("YYYYMMDD")
        : "";

      const paymentIndex = paymentMethodsDataTable.findIndex(
        (item: PaymentMethodsAdded) => item.key === paymentMethod?.key
      );
      const sequence = paymentIndex >= 0 ? paymentIndex + 1 : 1;

      const originalExtension = fileSelected[0].name?.split(".").pop()?.toLowerCase();
      const mimeType = fileSelected[0].type;
      const mimeExtension = mimeType?.split("/").pop()?.split(";")[0];
      const fileExtension = originalExtension || mimeExtension || "pdf";

      let newFileName = "";

      switch (codePaymentMethod) {
        case PAYMENT_METHODS.ELECTRONIC_CHECK:
          newFileName = `CHEQUEELECTRONICO_${preliquidationNumber}_${bankSelectedName}_${checkDate}_${sequence}.${fileExtension}`;
          break;
        case PAYMENT_METHODS.ROUNDING:
          newFileName = `REDONDEO_${preliquidationNumber}_${sequence}.${fileExtension}`;
          break;
        case PAYMENT_METHODS.BANK_DEPOSIT:
          newFileName = `RECIBOTRANSFERENCIA_${preliquidationNumber}_${bankSelectedName}_${transferDate}_${sequence}.${fileExtension}`;
          break;
        case PAYMENT_METHODS.GALICIA_INTEGRATED_COLLECTION:
          newFileName = `COBRANZAINTEGRADA_${preliquidationNumber}_${sequence}.${fileExtension}`;
          break;
        case PAYMENT_METHODS.TAX_WITHHOLDING:
          newFileName = `RETENCION_PRELIQUIDACION_${preliquidationNumber}_${sequence}.${fileExtension}`;
          break;
        default:
          newFileName = "Ningún archivo seleccionado...";
          break;
      }

      if (fileSelected[0]) {
        fileSelected[0].name = newFileName;
      }

      return newFileName;
    },
    [form, currentPaymentMethodCode, paymentMethod, preliquidationInfo, paymentMethodsDataTable, resolveBankCodeAndLabel]
  );

  const prepareFileProperties = (props: {
    fileSelectedName: string;
    newPaymentMethod: number;
    bankSelectedName: string;
    transferDate: string;
    checkDate: string;
    checkNumber: string;
    sequence: string;
    depositBankNumber: number;
  }) => {
    const preliquidationNumber = preliquidationInfo?.numeroPreliquidacion!;
    return {
      DocumentTitle: props.fileSelectedName,
      "cmis:name": props.fileSelectedName,
      "cmis:Preliquidacion": preliquidationNumber.toString(),
      ...(props.newPaymentMethod === PAYMENT_METHODS.ELECTRONIC_CHECK && {
        "cmis:Banco": props.bankSelectedName,
        "cmis:FechaTransferencia": props.checkDate,
        "cmis:NumeroCheque": props.checkNumber,
      }),
      ...(props.newPaymentMethod === PAYMENT_METHODS.TAX_WITHHOLDING && {
        "cmis:SecuenciaRetencion": props.sequence.toString(),
      }),
      ...(props.newPaymentMethod === PAYMENT_METHODS.BANK_DEPOSIT && {
        "cmis:Banco": props.bankSelectedName,
        "cmis:NroControl": props.depositBankNumber.toString(),
        "cmis:FechaTransferencia": props.transferDate,
      }),
    };
  };

  const transformFileSelected = (
    fileSelected: any,
    properties: Record<string, string>
  ): DocumentUploadServiceRequestParams => {
    const { base, mediaType, name } = fileSelected;
    const base64 = base.split(",")[1];

    const propertyString = Object.entries(properties).map(([key, value]) => ({
      id: key,
      type: "String" as "String",
      value: value,
    }));

    const userName =
      authContext?.getUserInfo()?.intermediarioPrincipal?.nombreInter;

    return {
      uploadDocument: {
        documentClass: DocumentClass.PaymentReceiptTransferPreliq,
        fileName: name,
        fileType: mediaType,
        base64: base64,
        applicationId: "WebProductores2.0",
        author: userName!,
        attributes: [
          { id: "cmis:objectTypeId", type: "String", value: DocumentClass.PaymentReceiptTransferPreliq },
          { id: "DocumentTitle", type: "String", value: name },
          { id: "cmis:name", type: "String", value: name },
          ...propertyString,
        ],
      },
    };
  };

  const handleCancel = useCallback(() => {
    form.resetFields();
    setFileSelected([]);
    onCancel();
  }, [form, onCancel]);

  const getCurrentProofLabel = useCallback(() => {
    if (fileSelected?.[0]) return generateFileNameForEdit(fileSelected);
    return "Comprobante cargado";
  }, [fileSelected, generateFileNameForEdit]);

  const handleSave = useCallback(async () => {
    if (!paymentMethod) return;

    try {
      setLoading(true);
      const values = await form.validateFields();
      const newPaymentMethod = Number(values.paymentMethod || currentPaymentMethodCode);
      const resolvedBank = resolveBankCodeAndLabel(values.bank, newPaymentMethod);
      const fileName = fileSelected?.[0] ? generateFileNameForEdit(fileSelected) : undefined;

      setPaymentMethodsDataTable((prev) =>
        prev.map((item) =>
          item.key === paymentMethod.key
            ? {
                ...item,
                exactAmount: values.exactAmount,
                bank: resolvedBank.label || item.bank,
                transferDate: values.transferDate
                  ? dayjs(values.transferDate).format("YYYY-MM-DD")
                  : item.transferDate,
                checkDate: values.checkDate
                  ? dayjs(values.checkDate).format("YYYY-MM-DD")
                  : item.checkDate,
                checkNumber: values.checkNumber || item.checkNumber,
                observations: values.observations || "-",
                proofOfPayment: fileName
                  ? (
                      <ActionButton
                        label={cutText(fileName, 20)}
                        icon={<PaperClipOutlined />}
                        color="danger"
                        type="text"
                        onClick={() => openFile(fileSelected[0])}
                      />
                    )
                  : item.proofOfPayment,
              }
            : item
        )
      );

      let fileTransformed: DocumentUploadServiceRequestParams | undefined;

      if (fileSelected?.[0] && fileName) {
        const bankSelectedName = (resolvedBank.label || "").replace(/\s+/g, "");
        const paymentIndex = paymentMethodsDataTable.findIndex(
          (item) => item.key === paymentMethod.key
        );
        const properties = prepareFileProperties({
          fileSelectedName: fileName,
          newPaymentMethod,
          bankSelectedName,
          transferDate: values.transferDate
            ? dayjs(values.transferDate).format("YYYY-MM-DD")
            : "",
          checkDate: values.checkDate
            ? dayjs(values.checkDate).format("YYYYMMDD")
            : "",
          checkNumber: values.checkNumber?.toString() || "",
          sequence: String(paymentIndex >= 0 ? paymentIndex + 1 : 1),
          depositBankNumber: 0,
        });
        fileTransformed = transformFileSelected(fileSelected[0], properties);
      }

      setPaymentsToLoad((prev) =>
        prev.map((payment: any) =>
          payment.newPaymentMethod === newPaymentMethod &&
          String(payment.basePaymentMethodData?.monto) === String(paymentMethod.exactAmount)
            ? {
                ...payment,
                basePaymentMethodData: {
                  ...payment.basePaymentMethodData,
                  codigoBanco: resolvedBank.code || payment.basePaymentMethodData?.codigoBanco,
                  fechaDeposito: values.transferDate
                    ? dayjs(values.transferDate).format("YYYY-MM-DD")
                    : payment.basePaymentMethodData?.fechaDeposito,
                  fecha: values.checkDate
                    ? dayjs(values.checkDate).format("YYYY-MM-DD")
                    : payment.basePaymentMethodData?.fecha,
                  monto: values.exactAmount?.toString(),
                  numeroCheque:
                    values.checkNumber?.toString() ??
                    payment.basePaymentMethodData?.numeroCheque,
                  observaciones: values.observations || "-",
                  ...(fileName ? { archivo: fileName } : {}),
                },
                ...(fileTransformed ? { fileToSend: fileTransformed } : {}),
              }
            : payment
        )
      );

      if (fileTransformed) {
        const fileToAppend = fileTransformed;
        setFilesToSend((prev) => [...prev, fileToAppend]);
      }

      setHasChanges(true);
      message.success("Comprobante actualizado correctamente.");
      onSuccess();
    } catch (error) {
      displayErrorMessage("No se pudo actualizar el comprobante.");
    } finally {
      setLoading(false);
    }
  }, [
    paymentMethod,
    form,
    currentPaymentMethodCode,
    fileSelected,
    generateFileNameForEdit,
    setPaymentMethodsDataTable,
    setPaymentsToLoad,
    paymentMethodsDataTable,
    prepareFileProperties,
    transformFileSelected,
    setFilesToSend,
    setHasChanges,
    cutText,
    openFile,
    onSuccess,
    resolveBankCodeAndLabel,
  ]);

  if (!paymentMethod) return null;

  return (
    <>
      <Modal
        centered
        open={open}
        onCancel={handleCancel}
        title="Editar Comprobante de Pago"
        width={800}
        footer={
          <>
            <ActionButton className="btn-principal-outline-barbuss" label="Cancelar" type="secondary" onClick={handleCancel} disabled={loading} />
            <ActionButton className="btn-principal-barbuss" label="Guardar" type="primary" loading={loading} onClick={handleSave} />
          </>
        }
      >
        <Form formProps={{ form, name: "edit-payment-method-form" }}>
          <ContentSection level="h4" title="Datos del Comprobante">
            <RowSize>
              <ColSize size={25}>
                <SelectForm
                  formProps={{
                    label: "Forma de Pago:",
                    name: "paymentMethod",
                    rules: validateForm.generateRules({ label: "forma de pago", rules: ["required"] }),
                  }}
                  selectProps={{ options: paymentMethods, disabled: true }}
                />
              </ColSize>

              <ColSize size={25}>
                <InputCurrencyForm
                  formProps={{
                    label: "Monto Exacto:",
                    name: "exactAmount",
                    rules: validateForm.generateRules({ label: "monto exacto", rules: ["required"] }),
                  }}
                  inputCurrencyProps={{ prefix: "$", placeholder: "Ingrese un monto" }}
                />
              </ColSize>

              {(Number(currentPaymentMethod) === PAYMENT_METHODS.BANK_DEPOSIT ||
                Number(currentPaymentMethod) === PAYMENT_METHODS.ELECTRONIC_CHECK) && (
                <ColSize size={25}>
                  <SelectForm
                    formProps={{
                      label: "Banco:",
                      name: "bank",
                      rules: validateForm.generateRules({ label: "banco", rules: ["required"] }),
                    }}
                    selectProps={{
                      options:
                        Number(currentPaymentMethod) === PAYMENT_METHODS.ELECTRONIC_CHECK
                          ? allBanksOptions || []
                          : banksOptions || [],
                      placeholder: "Seleccionar",
                    }}
                  />
                </ColSize>
              )}
            </RowSize>
          </ContentSection>
          <ContentSection level="h4" title="Comprobante de Pago">
            <RowSize>
              <ColSize size={50}>
                <FormAntd.Item label="Archivo">
                  <div className="container-pay-preliquidation">
                    <ActionButton
                      icon={<UploadOutlined />}
                      label="Subir"
                      type="secondary"
                      className="btn-accent-outline-barbuss button-add-payment-method"
                      onClick={() => setCanShowUploadModal(true)}
                    />
                    <ActionButton
                      label={getCurrentProofLabel()}
                      icon={<PaperClipOutlined />}
                      className="file-name"
                      color="default"
                      type="onlyTextBlack"
                      onClick={() =>
                        fileSelected?.[0]
                          ? openFile(fileSelected[0])
                          : setCanShowUploadModal(true)
                      }
                    />
                  </div>
                </FormAntd.Item>
              </ColSize>
            </RowSize>
          </ContentSection>
        </Form>
      </Modal>

      <UploadFileModal
        canShowUploadModalUploadFile={canShowUploadModal}
        setCanShowUploadModalUploadFile={setCanShowUploadModal}
        setFileSelect={setFileSelected}
        openFile={openFile}
      />
    </>
  );
};